import {
  ErrorTriageState,
  IErrorOccurrence,
  IErrorTriageListItem,
} from '@common/interfaces/errorTriage'
import { ILoadingData } from '../types'

export type IListFilters = Record<string, unknown>

/** What a loaded list answers; a load-more page for any other query is dropped. */
export interface IListQuery {
  filters: IListFilters
  orderBy: string
  orderDirection: string
}

export interface IErrorTriageListQuery extends IListQuery {
  state: ErrorTriageState
}

export interface IErrorOccurrenceListQuery extends IListQuery {
  /** Set for the drawer's table; absent for the All occurrences tab. */
  errorTriageId?: string
}

export interface IPageParams {
  page: number
  perPage: number
}

export interface IListOptions {
  loadMore?: boolean
  skipLoader?: boolean
}

export interface IPage<T> {
  data: T[]
  total: number
}

export type IPagedListState<T, Q extends IListQuery> = Omit<
  ILoadingData<IPage<T> | null>,
  'query'
> & {
  query?: Q
}

export type IErrorTriageListState = IPagedListState<IErrorTriageListItem, IErrorTriageListQuery>

export type IErrorOccurrenceListState = IPagedListState<IErrorOccurrence, IErrorOccurrenceListQuery>

const NO_ROWS: never[] = []

export const pageRows = <T>(list: IPagedListState<T, IListQuery>): T[] => list.data?.data ?? NO_ROWS

export const pageTotal = (list: IPagedListState<unknown, IListQuery>): number =>
  list.data?.total ?? 0

const withPage = <T, S extends IPagedListState<T, IListQuery>>(
  list: S,
  data: T[],
  total: number,
): S => ({
  ...list,
  data: { ...list.data, data, total },
})

/** Puts a mutated row back in place, or drops it when its new state leaves the loaded tab. */
export const replaceErrorTriageItem = (
  list: IErrorTriageListState,
  item: IErrorTriageListItem,
): IErrorTriageListState => {
  const { query, data: page } = list
  if (!query || !page) return list
  if (!page.data.some((row) => row.id === item.id)) return list
  if (item.state !== query.state) {
    return withPage(
      list,
      page.data.filter((row) => row.id !== item.id),
      Math.max(page.total - 1, 0),
    )
  }
  return withPage(
    list,
    page.data.map((row) => (row.id === item.id ? item : row)),
    page.total,
  )
}
